"use client";

import { useState } from "react";
import { FormLayout } from "../FormLayout";
import { ImageUpload } from "../ImageUpload";
import { ContactForm } from "./sections/ContactForm";
import { ClassifiedPreview } from "./sections/ClassifiedPreview";

export function AutoForm({
  step,
  maxSteps,
  onNext,
  onBack,
  onClose,
  formData,
  updateFormData,
  isProcessing,
}) {
  const [title, setTitle] = useState(formData.title || "");
  const [description, setDescription] = useState(formData.description || "");
  const [images, setImages] = useState(formData.images || []);
  const [kilometers, setKilometers] = useState(formData.kilometers || "");
  const [engineType, setEngineType] = useState(formData.engineType || "");
  const [price, setPrice] = useState(formData.price || "");
  const [condition, setCondition] = useState(formData.condition || "");
  const [story, setStory] = useState(formData.story || "");
  const [isPreowned, setIsPreowned] = useState(formData.isPreowned || null);

  // Validation errors state
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateStep = (stepNumber) => {
    const newErrors = {};

    switch (stepNumber) {
      case 1: // Photos
        if (images.length < 6) {
          newErrors.images = "At least 6 photos are required";
        }
        break;
      case 2: // Title
        if (!title.trim()) {
          newErrors.title = "Title is required";
        } else if (title.trim().length < 2) {
          newErrors.title = "Title must be at least 2 characters long";
        }
        break;
      case 3: // Key Details
        if (!condition) {
          newErrors.condition = "Condition rating is required";
        } else if (condition < 1 || condition > 10) {
          newErrors.condition = "Condition rating must be between 1 and 10";
        }
        if (!kilometers.trim()) {
          newErrors.kilometers = "Kilometers is required";
        } else if (isNaN(kilometers) || Number(kilometers) < 0) {
          newErrors.kilometers = "Please enter a valid number of kilometers";
        }
        if (!engineType) {
          newErrors.engineType = "Engine type is required";
        }
        if (isPreowned === null) {
          newErrors.isPreowned =
            "Please select if the vehicle is preowned or new";
        }
        break;
      case 4: // Price
        if (!price) {
          newErrors.price = "Price is required";
        } else if (price <= 0) {
          newErrors.price = "Price must be greater than 0";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Determine if Next button should be disabled
  const isNextDisabled = () => {
    switch (step) {
      case 1: // Photos
        return images.length < 6;
      case 2: // Title
        return !title.trim() || title.trim().length < 2;
      case 3: // Key Details
        return !condition || !kilometers || !engineType || isPreowned === null;
      case 4: // Price
        return !price || price <= 0;
      case 6: // Contact Form
        return (
          !formData.isContactFormValid ||
          !formData.fullName?.trim() ||
          !formData.email?.trim() ||
          !formData.phoneNumber?.trim() ||
          !formData.addressLine1?.trim() ||
          !formData.state?.trim()
        );
      default:
        return false;
    }
  };

  // Save data when moving between steps
  const handleNext = () => {
    if (!validateStep(step)) {
      return;
    }

    if (step === 3) {
      const specific_details = {
        condition: Number(condition),
        kilometers: Number(kilometers),
        engineType,
        isPreowned,
      };
      updateFormData({
        ...formData,
        specific_details,
      });
    } else if (step === 6) {
      updateFormData({
        ...formData,
        contactName: formData.contactName || "",
        contactEmail: formData.contactEmail || "",
        contactPhone: formData.contactPhone || "",
        preferredContact: formData.preferredContact || "",
        address: formData.address || "",
        city: formData.city || "",
        state: formData.state || "",
        zip: formData.zip || "",
        agreeTerms: formData.agreeTerms || false,
      });
    } else {
      updateFormData({
        title,
        description,
        images,
        kilometers: Number(kilometers),
        engineType,
        price: Number(price),
        condition: Number(condition),
        story,
        isPreowned,
      });
    }
    onNext();
  };

  const handleImagesChange = (newImages) => {
    setImages(newImages);
    if (newImages.length > 0 && errors.images) {
      setErrors((prev) => ({ ...prev, images: undefined }));
    }
  };

  const inputClassName =
    "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
  const errorInputClassName =
    "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

  const renderStepContent = () => {
    switch (step) {
      case 1: // Add Photos
        return (
          <div className="space-y-4">
            <ImageUpload
              category="Auto"
              minPhotos={6}
              images={images}
              onChange={handleImagesChange}
            />
            {errors.images && (
              <p className="text-red-500 text-sm mt-1">{errors.images}</p>
            )}
          </div>
        );

      case 2: // Add Title
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 font-fraunces"
              >
                Add title
              </label>
              <input
                id="title"
                type="text"
                placeholder="Enter a descriptive title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors((prev) => ({ ...prev, title: undefined }));
                  }
                }}
                className={errors.title ? errorInputClassName : inputClassName}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>
          </div>
        );

      case 3: // Key Details
        return (
          <div className="space-y-5">
            <h2 className="text-xl font-medium font-fraunces">
              Add key details about your Vehicle
            </h2>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Condition Rating (1-10) *
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={condition}
                onChange={(e) => {
                  setCondition(e.target.value);
                  if (errors.condition) {
                    setErrors((prev) => ({ ...prev, condition: undefined }));
                  }
                }}
                placeholder="Enter rating from 1-10"
                className={
                  errors.condition ? errorInputClassName : inputClassName
                }
              />
              {errors.condition && (
                <p className="text-red-500 text-sm mt-1">{errors.condition}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Engine Type *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="petrol"
                    name="engineType"
                    value="petrol"
                    checked={engineType === "petrol"}
                    onChange={(e) => {
                      setEngineType(e.target.value);
                      if (errors.engineType) {
                        setErrors((prev) => ({
                          ...prev,
                          engineType: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="petrol"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Petrol
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="diesel"
                    name="engineType"
                    value="diesel"
                    checked={engineType === "diesel"}
                    onChange={(e) => {
                      setEngineType(e.target.value);
                      if (errors.engineType) {
                        setErrors((prev) => ({
                          ...prev,
                          engineType: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="diesel"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Diesel
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="ev"
                    name="engineType"
                    value="ev"
                    checked={engineType === "ev"}
                    onChange={(e) => {
                      setEngineType(e.target.value);
                      if (errors.engineType) {
                        setErrors((prev) => ({
                          ...prev,
                          engineType: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="ev"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Electric Vehicle (EV)
                  </label>
                </div>
              </div>
              {errors.engineType && (
                <p className="text-red-500 text-sm mt-1">{errors.engineType}</p>
              )}
            </div>

            <div className="space-y-3">
              <label
                htmlFor="kilometers"
                className="block text-sm font-medium text-gray-700"
              >
                Kilometers *
              </label>
              <input
                id="kilometers"
                type="number"
                min="0"
                placeholder="Enter kilometers"
                value={kilometers}
                onChange={(e) => {
                  setKilometers(e.target.value);
                  if (errors.kilometers) {
                    setErrors((prev) => ({ ...prev, kilometers: undefined }));
                  }
                }}
                className={
                  errors.kilometers ? errorInputClassName : inputClassName
                }
              />
              {errors.kilometers && (
                <p className="text-red-500 text-sm mt-1">{errors.kilometers}</p>
              )}
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Is the vehicle preowned or new? *
              </label>
              <div className="flex gap-6">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="preowned-vehicle"
                    name="isPreownedVehicle"
                    checked={isPreowned === true}
                    onChange={() => {
                      setIsPreowned(true);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="preowned-vehicle"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    Preowned
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="new-vehicle"
                    name="isPreownedVehicle"
                    checked={isPreowned === false}
                    onChange={() => {
                      setIsPreowned(false);
                      if (errors.isPreowned) {
                        setErrors((prev) => ({
                          ...prev,
                          isPreowned: undefined,
                        }));
                      }
                    }}
                    className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                  />
                  <label
                    htmlFor="new-vehicle"
                    className="ml-3 block text-sm font-medium text-gray-700"
                  >
                    New
                  </label>
                </div>
              </div>
              {errors.isPreowned && (
                <p className="text-red-500 text-sm mt-1">{errors.isPreowned}</p>
              )}
            </div>
          </div>
        );

      case 4: // Price
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Select your perfect price
            </h2>
            <h4 className="text-sm font-medium font-afacad text-gray-500">
              Condition, features, and more - help others see the value!
            </h4>
            <div className="space-y-2">
              <label
                htmlFor="price"
                className="block text-sm font-medium text-gray-700"
              >
                Price *
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                placeholder="$ Enter the amount"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (errors.price) {
                    setErrors((prev) => ({ ...prev, price: undefined }));
                  }
                }}
                className={errors.price ? errorInputClassName : inputClassName}
              />
              {errors.price && (
                <p className="text-red-500 text-sm mt-1">{errors.price}</p>
              )}
            </div>
          </div>
        );

      case 5: // Tell story
        return (
          <div className="space-y-4">
            <h2 className="text-lg font-medium font-fraunces">
              Tell the story behind your Vehicle
            </h2>
            <p className="text-sm text-gray-500">
              Why sell? Share the details!
            </p>
            <div className="space-y-2">
              <label
                htmlFor="story"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for selling / Additional details
              </label>
              <textarea
                id="story"
                rows={6}
                placeholder="Tell your story..."
                value={story}
                onChange={(e) => setStory(e.target.value)}
                className={inputClassName}
              />
            </div>
            <div className="space-y-2">
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700"
              >
                More Description
              </label>
              <textarea
                id="description"
                rows={6}
                placeholder="Add a personal touch - what should buyers know?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={inputClassName}
              />
            </div>
          </div>
        );

      case 6: // Contact details
        return (
          <ContactForm formData={formData} updateFormData={updateFormData} />
        );
      case 7: // Preview
        return <ClassifiedPreview formData={formData} category="Auto" />;
      default:
        return null;
    }
  };

  // Get step-specific title and description
  const getStepInfo = () => {
    const title = "Create Classified";
    let description = "";

    switch (step) {
      case 1:
        description = "Add photos of your Vehicle";
        break;
      case 2:
        description = "Add title and description";
        break;
      case 3:
        description = "Add key details";
        break;
      case 4:
        description = "Set price";
        break;
      case 5:
        description = "Tell your story";
        break;
      case 6:
        description = "Add your contact details";
        break;
      case 7:
        description = "Preview your classified";
        break;
    }

    return { title, description };
  };

  const { title: stepTitle, description: stepDescription } = getStepInfo();

  return (
    <FormLayout
      title={stepTitle}
      description={stepDescription}
      step={step}
      maxSteps={maxSteps}
      onNext={handleNext}
      onBack={onBack}
      onClose={onClose}
      isLastStep={step === maxSteps}
      isNextDisabled={isNextDisabled()}
      isProcessing={isProcessing}
    >
      {renderStepContent()}
    </FormLayout>
  );
}
